import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import useOrdersQuery from "../queries/useOrdersQuery";
import type { ICustomerOrdersQuery, IOrdersEdgesNode } from "../types/api/customer";
import { OrderSortKeys } from "../types/generated/storefront.types";

interface IProps {
  orderId: string | undefined;
  accessToken: string;
  cacheFirst?: number; // 캐시 조회
  fetchFirst?: number; // 네트워크 조회
  lang?: "ko" | "en" | "jp";
  openToast?: (toast: any) => void;
}

interface IOrderReturn {
  order: IOrdersEdgesNode | null;
  loading: boolean;
  orderNotFound: boolean;
}

export default function useFindOrder({
  orderId,
  accessToken,
  cacheFirst = 10,
  fetchFirst = 250,
  lang = "ko",
  openToast = () => {},
}: IProps): IOrderReturn {
  // 공통 queries 훅(useOrdersQuery)의 fetchOrders 재사용.
  const { fetchOrders } = useOrdersQuery({ lang, openToast, accessToken, first: fetchFirst });
  const queryClient = useQueryClient();

  const [cachedData, setCachedData] = useState<ICustomerOrdersQuery>();
  const [ordersData, setOrdersData] = useState<ICustomerOrdersQuery>();
  const [cachedDataLoading, setCachedDataLoading] = useState(true);
  const [ordersDataLoading, setOrdersDataLoading] = useState(true);

  // Step 1: 캐시에서 주문 찾기 (Apollo fetchPolicy: "cache-only" 대응)
  useEffect(() => {
    // Apollo 의 skip 과 같은 조건. 조회를 건너뛰면 "로딩 아님"으로 내려야 한다 —
    // 그냥 return 하면 초기값 true 가 그대로 남아 loading 이 영영 안 풀린다(fetchFirst: 1 인 주문 완료 페이지)
    if (!orderId || fetchFirst === 1) {
      setCachedDataLoading(false);
      return;
    }

    setCachedDataLoading(true);

    const queryKey = ["shopify", "orders", { accessToken, first: cacheFirst }];
    const cachedInfiniteData = queryClient.getQueryData<{ pages: ICustomerOrdersQuery[] }>(queryKey);
    const pages = cachedInfiniteData?.pages;

    if (pages?.length) {
      const firstCustomer = pages[0]?.customer;
      if (!firstCustomer) {
        setCachedData(pages[0]);
      } else {
        const lastCustomer = pages[pages.length - 1].customer;
        setCachedData({
          customer: {
            ...lastCustomer,
            orders: {
              ...lastCustomer.orders,
              edges: pages.flatMap((page) => page.customer?.orders?.edges ?? []),
              pageInfo: lastCustomer.orders.pageInfo,
            },
          },
        } as ICustomerOrdersQuery);
      }
    } else {
      setCachedData(undefined);
    }

    setCachedDataLoading(false);
  }, [orderId, accessToken, cacheFirst, fetchFirst, queryClient]);

  const cachedOrder = useMemo(() => {
    if (!orderId || !cachedData?.customer?.orders?.edges) return null;
    return cachedData.customer.orders.edges.find(({ node }) => node.id.includes(`gid://shopify/Order/${orderId}`))?.node;
  }, [orderId, cachedData]);

  // Step 2: 캐시에 없으면 네트워크에서 조회
  useEffect(() => {
    if (!orderId) {
      setOrdersDataLoading(false);
      return;
    }

    setOrdersDataLoading(true);
    void fetchOrders({
      customerAccessToken: accessToken,
      first: fetchFirst,
      sortKey: OrderSortKeys.ProcessedAt,
      reverse: true,
    })
      .then((data) => {
        setOrdersData(data ?? undefined);
      })
      .catch(() => undefined)
      .finally(() => {
        setOrdersDataLoading(false);
      });
  }, [orderId, accessToken, fetchFirst]);

  // 캐시 또는 네트워크에서 주문 찾기
  const order = useMemo(() => {
    if (!orderId) return null;

    // cachedOrder 우선 사용
    if (cachedOrder) return cachedOrder as IOrdersEdgesNode;

    if (!ordersData?.customer.orders.edges) return null;

    // ordersData에서 찾기
    return ordersData.customer.orders.edges.find(({ node }) => node.id.includes(`gid://shopify/Order/${orderId}`))
      ?.node as IOrdersEdgesNode;
  }, [orderId, cachedOrder, ordersData]);

  // 250개 안에서 못 찾으면 orderNotFound 설정
  const orderNotFound = useMemo(() => {
    if (!orderId || cachedDataLoading || ordersDataLoading) return false;
    if (cachedOrder) return false;

    if (!ordersData?.customer.orders.edges) return false;

    const matchedOrder = ordersData.customer.orders.edges.find(({ node }) => node.id.includes(`gid://shopify/Order/${orderId}`));

    return !matchedOrder;
  }, [orderId, cachedOrder, cachedDataLoading, ordersDataLoading, ordersData]);

  return {
    order,
    loading: ordersDataLoading || cachedDataLoading,
    orderNotFound, // 250개 안에서 못 찾음 -> 이때 useOreDetail 훅에서 Admin API 실행
  };
}
